<template>
    <v-container class="my-campaign-page" :class="{ 'pa-4': $vuetify.breakpoint.smAndDown }">
        <v-card elevation="0" class="campaign-card mx-auto" max-width="800">
            <!-- Page Header -->
            <div class="page-header pa-6 pa-md-8">
                <h1 class="page-title">My Campaign</h1>
                <p class="page-subtitle">
                    Let's start college on the right note. Spread the word and get friends and family to help out!
                </p>
            </div>

            <!-- Stripe Not Connected -->
            <div v-if="!isUserOnboarded" class="px-6 px-md-8 py-6">
                <v-card elevation="0" outlined class="pa-6 text-center stripe-card">
                    <v-icon size="48" color="grey lighten-1" class="mb-4">mdi-credit-card-outline</v-icon>

                    <!-- User has partial Stripe setup -->
                    <template v-if="user.stripe">
                        <h2 class="mb-3">Finish your Stripe onboarding</h2>
                        <p class="mb-4">You're almost there! Complete your Stripe onboarding to start your campaign.</p>
                        <v-btn color="primary" depressed @click="finishOnboarding" :loading="isConnectingStripe">
                            Finish Onboarding
                        </v-btn>
                    </template>

                    <!-- User has no Stripe at all -->
                    <template v-else>
                        <h2 class="mb-3">Connect to Stripe to Get Started</h2>
                        <p class="mb-4">To start a fundraising campaign and receive donations, you'll need to connect your Stripe account.</p>
                        <AddStripeAccountDialog />
                    </template>
                </v-card>
            </div>

            <!-- Has Campaign -->
            <template v-else-if="campaign">
                <!-- Campaign Management Header -->
                <div class="campaign-management px-6 px-md-8 py-4">
                    <v-card elevation="0" class="management-card pa-5">
                        <v-row>
                            <v-col cols="12" md="8">
                                <div class="d-flex align-center mb-3">
                                    <v-chip
                                        color="success"
                                        small
                                        class="mr-2"
                                    >
                                        <v-icon left x-small>mdi-circle</v-icon>
                                        Active
                                    </v-chip>
                                    <v-chip
                                        v-if="isItemizedCampaign"
                                        color="primary"
                                        small
                                        outlined
                                        class="mr-3"
                                    >
                                        <v-icon left x-small>mdi-format-list-checks</v-icon>
                                        Itemized
                                    </v-chip>
                                    <span class="campaign-goal-text">Goal: ${{ campaign.amount }}</span>
                                </div>
                                <div class="management-info">
                                    <div class="info-item">
                                        <v-icon size="18" color="grey darken-1" class="mr-2">mdi-calendar-clock</v-icon>
                                        <span class="info-label">Funds arrive:</span>
                                        <span class="info-value">
                                            {{ campaign.deposit_type === 'delay' ? campaignEndDate : 'When goal is reached' }}
                                        </span>
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="12" md="4" class="d-flex flex-column justify-center">
                                <v-btn
                                    color="primary"
                                    depressed
                                    class="mb-2"
                                    @click="showEditCampaignDialog = true"
                                >
                                    <v-icon left small>mdi-pencil</v-icon>
                                    Edit Campaign
                                </v-btn>
                                <v-btn
                                    outlined
                                    color="success"
                                    @click="showEndCampaignDialog = true"
                                >
                                    <v-icon left small>mdi-cash-check</v-icon>
                                    Claim Funds
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card>
                </div>

                <!-- Progress Section -->
                <div class="px-6 px-md-8 py-4">
                    <CampaignProgress
                        :donations="campaign.donations"
                        :goal="campaign.amount"
                    />
                </div>

                <!-- Itemized Progress Section -->
                <div v-if="isItemizedCampaign" class="px-6 px-md-8 py-4">
                    <CampaignItemsProgress
                        :items="campaign.items"
                    />
                </div>

                <!-- Share Section -->
                <div class="px-6 px-md-8 py-4">
                    <CampaignShare
                        :share-url="campaignUrl"
                        :user-name="user.name"
                        title="Share with others"
                        description="The more you share, the easier and faster it will be to reach your goal."
                    />
                </div>

                <!-- Recent Donations -->
                <div class="px-6 px-md-8 py-4 pb-8">
                    <CampaignDonations
                        :donations="campaign.donations"
                        title="Recent Activity"
                        empty-message="No donations yet. Share your campaign to start receiving donations!"
                        :is-owner="true"
                    />
                </div>
            </template>

            <!-- No Campaign Yet -->
            <div v-else class="px-6 px-md-8 py-6">
                <v-card elevation="0" outlined class="pa-6 text-center no-campaign-card">
                    <v-icon size="48" color="grey lighten-1" class="mb-4">mdi-bullhorn-outline</v-icon>
                    <h2 class="mb-3">You haven't started a campaign yet</h2>
                    <p class="mb-4">When you create a campaign, it will appear here.</p>
                    <v-btn
                        color="primary"
                        depressed
                        @click="showCampaign = true"
                        width="200"
                    >
                        Start a Campaign
                    </v-btn>
                </v-card>
            </div>

            <!-- FAQ Section -->
            <div class="px-6 px-md-8 py-4 pb-8" v-if="page && page.length">
                <FaqCollapsible :page="page" />
            </div>
        </v-card>

        <!-- Create Campaign Dialog -->
        <v-dialog v-model="showCampaign" max-width="540">
            <Campaign
                @hideDialog="showCampaign = false"
                @getCampaign="getCampaign"
            />
        </v-dialog>

        <!-- Edit Campaign Dialog -->
        <v-dialog v-model="showEditCampaignDialog" max-width="540">
            <EditCampaign
                v-if="campaign"
                :campaign="campaign"
                @hideDialog="showEditCampaignDialog = false"
                @getCampaign="getCampaign"
            />
        </v-dialog>

        <!-- End Campaign Dialog -->
        <v-dialog v-model="showEndCampaignDialog" max-width="540">
            <EndCampaign
                v-if="campaign"
                :campaign="campaign"
                @hideDialog="showEndCampaignDialog = false"
                @getCampaign="getCampaign"
            />
        </v-dialog>

    </v-container>
</template>

<script>
import moment from 'moment'
import Campaign from '@/components/Campaign.vue'
import EditCampaign from '@/components/EditCampaign.vue'
import EndCampaign from '@/components/EndCampaign.vue'
import CampaignProgress from '@/components/campaign/CampaignProgress.vue'
import CampaignItemsProgress from '@/components/campaign/CampaignItemsProgress.vue'
import CampaignShare from '@/components/campaign/CampaignShare.vue'
import CampaignDonations from '@/components/campaign/CampaignDonations.vue'
import FaqCollapsible from '@/components/FaqCollapsible.vue'
import AddStripeAccountDialog from '@/components/AddStripeAccountDialog.vue'
import UserService from '@/services/user.service'
import SiteService from '@/services/site.service'
import PaymentService from '@/services/payment.service'

export default {
    name: 'MyCampaign',
    components: {
        Campaign,
        EditCampaign,
        EndCampaign,
        CampaignProgress,
        CampaignItemsProgress,
        CampaignShare,
        CampaignDonations,
        FaqCollapsible,
        AddStripeAccountDialog
    },
    data: () => ({
        showCampaign: false,
        showEditCampaignDialog: false,
        showEndCampaignDialog: false,
        campaign: null,
        isCallingApi: false,
        isConnectingStripe: false,
        page: []
    }),
    watch: {
        user(value) {
            if (value !== {}) {
                this.getCampaign()
            }
        }
    },
    computed: {
        user() {
            return this.$store.getters.getUser
        },
        campaignEndDate() {
            return moment(this.user.university_start_date).subtract(14, 'd').format('L')
        },
        isUserOnboarded() {
            return this.user.stripe && this.user.stripe.onboarding_completed
        },
        campaignUrl() {
            if (!this.campaign) return ''
            return `${process.env.VUE_APP_CLIENT_BASE_URL}/campaign/${this.campaign.hash}`
        },
        isItemizedCampaign() {
            return this.campaign?.is_itemized && this.campaign?.items?.length > 0
        }
    },
    methods: {
        async getCampaign() {
            this.isCallingApi = true
            this.campaign = await UserService.getCampaign(this.user.id, '?appendUser&appendDonations&appendItems')
            this.isCallingApi = false
        },
        async finishOnboarding() {
            this.isConnectingStripe = true
            try {
                const accountLink = await PaymentService.getAccountLink()
                if (accountLink && accountLink.url) {
                    window.location.href = accountLink.url
                }
            } catch {
                this.$store.dispatch('getUserByToken')
            }
            this.isConnectingStripe = false
        }
    },
    async beforeMount() {
        this.getCampaign()
        this.page = await SiteService.getMyFundsPage()
    }
}
</script>

<style scoped>
.my-campaign-page {
    max-width: 900px;
    padding-top: 24px;
    padding-bottom: 48px;
}

.campaign-card {
    border-radius: 12px;
    overflow: hidden;
}

.page-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.page-title {
    font-size: 28px;
    font-weight: 700;
    color: #333;
    margin-bottom: 8px;
}

.page-subtitle {
    font-size: 16px;
    color: #666;
    margin: 0;
}

.stripe-card,
.no-campaign-card {
    border-radius: 10px !important;
}

.stripe-card h2,
.no-campaign-card h2 {
    font-size: 20px;
    font-weight: 700;
    color: #333;
}

.stripe-card p,
.no-campaign-card p {
    font-size: 14px;
    color: #666;
}

.management-card {
    border-radius: 12px !important;
    background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
    border: 1px solid #e9ecef !important;
}

.campaign-goal-text {
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.management-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-item {
    display: flex;
    align-items: center;
    font-size: 14px;
}

.info-label {
    color: #666;
    margin-right: 6px;
}

.info-value {
    color: #333;
    font-weight: 500;
}

@media (max-width: 600px) {
    .page-title {
        font-size: 24px;
    }

    .page-subtitle {
        font-size: 14px;
    }
}
</style>
